import { Directive, inject } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { BiCountryId, BiLanguageId } from "@globals/enums/BiLanguageAndCountryId";
import { cloneObject } from "@globals/helper-functions";
import { SubscriptionUrlParams } from "@globals/models/SubscriptionUrlParams";
import { SubscriptionDto } from "@globals/openapi-models/model/subscriptionDto";
import { LanguageConfig } from "@globals/services/bi-localization-helper-base.service";
import { Observable, of, take, tap } from "rxjs";

export type SubscriberInfo = {
  mobileNumber?: string;
  landlineNumber?: string;
  email?: string;
  name?: string;
  accessToken?: string;
};

/**
 * Base class used for shared services in Subscription modules
 */
@Directive()
export abstract class SubscriptionModuleBaseSharedService {
  public refreshData(): Observable<SubscriptionDto[]> {
    return of([]);
  }

  public countryId: BiCountryId = BiCountryId.DK;
  public customerId: number;
  /**
   * The language configurations based on customer's language
   */
  public languageConfig: LanguageConfig;

  public urlParams: SubscriptionUrlParams;
  public disclaimerText: string;
  public allowOnlySingleContactInfo: boolean;

  protected currentSubscriberInfo: SubscriberInfo;

  protected activeRoute: ActivatedRoute;
  public constructor() {
    this.activeRoute = inject(ActivatedRoute);
  }

  public setSubscriberInfo(info: SubscriberInfo) {
    this.currentSubscriberInfo = info;
  }

  public getCurrentSubscriberInfo(): SubscriberInfo {
    return this.currentSubscriberInfo;
  }

  public getAndSetUrlParams() {
    return this.activeRoute.queryParams.pipe(
      take(1),
      tap(params => {
        this.urlParams = params;
        this.correctUrlColorParams();
      })
    );
  }

  /**
   * For getting the module's settings by customer public id
   */
  public abstract initModuleConfigObject(publicId: string, languageId?: BiLanguageId): any;

  /**
   * For getting the data to display in a list of subscriptions (the subscription data).
   */
  public abstract getSubscriptionListData(onlyBusiness?: boolean, onlyPrivate?: boolean);

  /**
   * Checks all URL params used for setting color styling that if they do not have a #-sign at the beginning, it'll be added
   * so that correct HEX-value is ued in CSS
   */
  private correctUrlColorParams() {
    const urlParamsClone = cloneObject(this.urlParams);

    urlParamsClone.referrer = window?.document?.referrer;

    if (window?.location?.href.indexOf("std-receivers") > -1) {
      urlParamsClone.module = "Std-receivers";
    } else {
      urlParamsClone.module = "Subscription";
    }

    if (this.urlParams.btnColor && this.urlParams.btnColor.indexOf("#") === -1) urlParamsClone.btnColor = "#" + this.urlParams.btnColor;
    if (this.urlParams.btnFontColor && this.urlParams.btnFontColor.indexOf("#") === -1) urlParamsClone.btnFontColor = "#" + this.urlParams.btnFontColor;

    if (this.urlParams.backgroundColor && this.urlParams.backgroundColor.indexOf("#") === -1) urlParamsClone.backgroundColor = "#" + this.urlParams.backgroundColor;
    if (this.urlParams.backColor && this.urlParams.backColor.indexOf("#") === -1) urlParamsClone.backColor = "#" + this.urlParams.backColor;

    if (this.urlParams.headerFontColor && this.urlParams.headerFontColor.indexOf("#") === -1) urlParamsClone.headerFontColor = "#" + this.urlParams.headerFontColor;

    if (this.urlParams.headerColor && this.urlParams.headerColor.indexOf("#") === -1) urlParamsClone.headerColor = "#" + this.urlParams.headerColor;

    if (this.urlParams.fontColor && this.urlParams.fontColor.indexOf("#") === -1) urlParamsClone.fontColor = "#" + this.urlParams.fontColor;

    if (this.urlParams.btnHoverColor && this.urlParams.btnHoverColor.indexOf("#") === -1) urlParamsClone.btnHoverColor = "#" + this.urlParams.btnHoverColor;

    if (this.urlParams.borderColor && this.urlParams.borderColor.indexOf("#") === -1) urlParamsClone.borderColor = "#" + this.urlParams.borderColor;
    if (this.urlParams.thankYouTextColor && this.urlParams.thankYouTextColor.indexOf("#") === -1) urlParamsClone.thankYouTextColor = "#" + this.urlParams.thankYouTextColor;
    if (this.urlParams.disclaimerTextColor && this.urlParams.disclaimerTextColor.indexOf("#") === -1) urlParamsClone.disclaimerTextColor = "#" + this.urlParams.disclaimerTextColor;

    this.urlParams = urlParamsClone;
  }
}
